import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Image as ImageIcon, Film, FileText } from "lucide-react";
import IdCell from "./IdCell";
import { tenantDisplayName } from "./tenantDisplay";
import { formatMoneyCode } from "../lib/formatMoney";
import { EMPTY, formatDate, formatDateTime } from "../lib/dateTime";
import { safeMediaSrc } from "../lib/platformMedia";

/**
 * Cell renderers for the Global Data tables. Each takes the value the backend sent and
 * renders exactly that: the row mappers (docs/auth-and-admin/global-data-contract.md section 2)
 * own the shape, so nothing here guesses a field name. An absent value is ALWAYS the same dash.
 */

export const DASH = "—";
const LONG_TEXT_THRESHOLD = 80;
const BADGE = "text-xs rounded-full px-2 py-0.5 bg-slate-100 text-slate-600";
const BADGE_TONES = {
  good: "bg-emerald-100 text-emerald-700",
  warn: "bg-amber-100 text-amber-700",
  bad: "bg-rose-100 text-rose-700",
};

const present = (v) => v !== null && v !== undefined && v !== "";

export function Text({ value, className = "" }) {
  return present(value) ? <span className={className}>{value}</span> : <span className="text-slate-300">{DASH}</span>;
}

/** `{cents, currency}` or null. Zero is an amount, never a missing one. */
export function Money({ value }) {
  if (!value || !Number.isFinite(Number(value.cents))) return <span className="text-slate-300">{DASH}</span>;
  // The number and the code are separate no-wrap runs joined by a breakable space, so a
  // narrow cell wraps BETWEEN them ("123456789.00" / "USD"); the cell's `break-words` would
  // otherwise split the number itself, and "1234567|89.00" reads as two different amounts.
  const [amount, ...code] = formatMoneyCode(value.cents, value.currency).split(" ");
  return (
    <span className="tabular-nums">
      <span className="whitespace-nowrap" data-money-part="amount">{amount}</span>
      {code.length > 0 && <>{" "}<span className="whitespace-nowrap" data-money-part="currency">{code.join(" ")}</span></>}
    </span>
  );
}

export function DateText({ value, lang, withTime = false }) {
  const text = (withTime ? formatDateTime : formatDate)(value, lang);
  if (text === EMPTY) return <span className="text-slate-300">{DASH}</span>;
  return <span className="text-xs text-slate-400">{text}</span>;
}

/**
 * A promo deadline. A row whose stored deadline no parser could read was repaired to the
 * 1970 epoch by the boot migration (the code stays expired); the backend flags it, and the
 * page says so rather than presenting the epoch as a date the practitioner chose.
 */
export function ExpiryText({ value, unreadable, unreadableLabel, lang }) {
  if (unreadable) return <span className="text-xs text-slate-400">{unreadableLabel}</span>;
  return <DateText value={value} lang={lang} />;
}

/**
 * A value from a closed vocabulary, shown through its i18n label. A code the page does not
 * know yet renders as the raw code (the third argument of `t`), never as a missing-key echo.
 */
export function Badge({ value, labelKey, t, tone }) {
  if (!present(value)) return <span className="text-slate-300">{DASH}</span>;
  return <span className={`${BADGE} ${BADGE_TONES[tone] || ""}`}>{t(labelKey, undefined, String(value))}</span>;
}

/** The practice a row belongs to: a link to its detail page, or a dash when it has none. */
export function TenantCell({ tenant }) {
  if (!tenant || !tenant.tenant_id) return <span className="text-slate-300">{DASH}</span>;
  return (
    <Link to={`/super-admin/tenants/${tenant.tenant_id}`} className="text-xs text-amber-700 hover:underline">
      {tenantDisplayName(tenant)}
    </Link>
  );
}

/** Text that may be long: truncated with the full value in `title`, expandable in place. */
export function LongText({ value, t }) {
  const [open, setOpen] = useState(false);
  if (!present(value)) return <span className="text-slate-300">{DASH}</span>;
  const canExpand = value.length > LONG_TEXT_THRESHOLD;
  return (
    <div className="text-xs text-slate-700">
      <span
        title={value}
        data-expanded={String(open)}
        className={open ? "block whitespace-pre-wrap [overflow-wrap:anywhere]" : "block truncate"}
      >
        {value}
      </span>
      {canExpand && (
        <button
          type="button"
          data-testid="gdv-expand"
          onClick={() => setOpen((v) => !v)}
          className="mt-1 text-amber-700 underline"
        >
          {open ? t("gdv.collapse") : t("gdv.expand")}
        </button>
      )}
    </div>
  );
}

export function Ref({ value }) {
  return <IdCell value={value} width="min-w-0" />;
}

const KIND_ICONS = { image: ImageIcon, video: Film, document: FileText };

/** An image shows its thumbnail (public only: private rows carry no url); everything else a type icon. */
export function MediaPreview({ row }) {
  const src = safeMediaSrc(row.url);
  if (row.kind === "image" && src) {
    return <img src={src} alt="" className="w-10 h-10 object-cover rounded" />;
  }
  const Icon = KIND_ICONS[row.kind] || FileText;
  return (
    <span
      data-testid={`gdv-media-icon-${row.kind || "document"}`}
      className="w-10 h-10 rounded bg-slate-50 border border-slate-200 flex items-center justify-center text-slate-400"
    >
      <Icon className="w-4 h-4" />
    </span>
  );
}

/**
 * A public file links to its address (`safeMediaSrc`: loadable from the SPA's origin, web
 * or `/api` addresses only); a private one is a badge and no link, and an address that
 * fails the guard leaves the name as plain text.
 */
export function MediaName({ row, t }) {
  if (!present(row.name)) return <span className="text-slate-300">{DASH}</span>;
  if (row.visibility === "private") {
    return (
      <span className="text-xs">
        <span className="font-medium">{row.name}</span>{" "}
        <span className={BADGE}>{t("gdv.visibility.private")}</span>
      </span>
    );
  }
  const href = safeMediaSrc(row.url);
  if (!href) return <span className="text-xs font-medium">{row.name}</span>;
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className="text-xs font-medium text-amber-700 hover:underline">
      {row.name}
    </a>
  );
}

/** Bytes as B / KB / MB. */
export function formatBytes(n) {
  const v = Number(n);
  if (!Number.isFinite(v) || n === null || n === "") return null;
  if (v < 1024) return `${v} B`;
  if (v < 1024 * 1024) return `${(v / 1024).toFixed(1)} KB`;
  return `${(v / (1024 * 1024)).toFixed(1)} MB`;
}

export function ColorSwatches({ row }) {
  const colors = [row.primary_color, row.accent_color, row.secondary_color].filter(Boolean);
  if (!colors.length) return <span className="text-slate-300">{DASH}</span>;
  return (
    <div className="flex gap-1.5">
      {colors.map((c) => (
        <span key={c} className="w-5 h-5 rounded border border-slate-200" style={{ background: c }} title={c} />
      ))}
    </div>
  );
}

export function Logo({ url }) {
  const src = safeMediaSrc(url);
  return src
    ? <img src={src} alt="" className="h-8 max-w-full object-contain" />
    : <span className="text-slate-300">{DASH}</span>;
}
